'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import PowerBIDashboard from './PowerBIDashboard';
import { MapType } from '@/lib/power-bi/types';
import CustomTabs from '../IframeTabs/CustomTabs';

interface PowerBIDashboardIndicadoresProps {
 initialMapType?: MapType;
 className?: string;
 tabs?: {
   title: string;
   content: React.ReactNode;
 }[];
}

export default function PowerBIDashboardIndicadores({ 
 initialMapType = 'Continental',
 className,
 tabs = []
}: PowerBIDashboardIndicadoresProps) {
 const [mapType, setMapType] = useState<MapType>(initialMapType);
 const [navigateFunction, setNavigateFunction] = useState<((type: MapType) => Promise<boolean>) | null>(null);
 const [isNavigating, setIsNavigating] = useState(false);
 
 const navigationTimeoutRef = useRef<NodeJS.Timeout>(null);
 const tabChangeTimeoutRef = useRef<NodeJS.Timeout>(null);
 const lastNavigationRef = useRef<MapType>(initialMapType);

 useEffect(() => {
   return () => {
     if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
     if (tabChangeTimeoutRef.current) clearTimeout(tabChangeTimeoutRef.current);
   };
 }, []);

 const clearNavigationState = useCallback(() => {
   if (navigationTimeoutRef.current) {
     clearTimeout(navigationTimeoutRef.current);
   }
   
   navigationTimeoutRef.current = setTimeout(() => {
     setIsNavigating(false);
   }, 1500);
 }, []);

 const handleReportReady = useCallback((report: any, utils?: { navigateToPage: (type: MapType) => Promise<boolean> }) => {
   if (utils?.navigateToPage) {
     setNavigateFunction(() => utils.navigateToPage);
   }
 }, []);

 const handleMapTypeChange = useCallback(async (newMapType: MapType) => {
   if (newMapType === mapType || isNavigating) {
     return;
   }
   
   lastNavigationRef.current = newMapType;
   
   if (!navigateFunction) {
     setMapType(newMapType);
     return;
   }
   
   setIsNavigating(true);
   
   try {
     const success = await navigateFunction(newMapType);
     if (success) {
       setMapType(newMapType);
       setTimeout(() => setIsNavigating(false), 200);
       return;
     }
   } catch (error) {
     console.error("❌ Erro na navegação:", error);
   }
   
   setMapType(newMapType);
   setNavigateFunction(null);
   clearNavigationState();
 }, [mapType, navigateFunction, isNavigating, clearNavigationState]);

 const handleTabChange = useCallback((tabIndex: number) => {
   if (tabChangeTimeoutRef.current) {
     clearTimeout(tabChangeTimeoutRef.current);
   }
   
   tabChangeTimeoutRef.current = setTimeout(() => {
     const newMapType: MapType = tabIndex === 0 ? 'Continental' : 'Madeira';
     handleMapTypeChange(newMapType);
   }, 100);
 }, [handleMapTypeChange]);

 const dashboardContent = (
   <div className="dashboard-content" style={{ height: '100%', position: 'relative' }}>
     {isNavigating && (
       <div 
         className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10"
         style={{ 
           backdropFilter: 'blur(1px)',
           transition: 'opacity 0.2s ease-in-out'
         }}
       >
         <div className="text-center">
           <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600 mb-2"></div>
           <p className="text-sm text-gray-600">Navegando...</p>
         </div>
       </div>
     )}
     
     <PowerBIDashboard 
       dashboardType="indicadores" 
       mapType={mapType}
       onReportReady={handleReportReady}
       className={isNavigating ? 'pointer-events-none' : ''}
     />
   </div>
 );

 const tabData = [
   {
     title: tabs[0]?.title || 'Continental',
     content: dashboardContent
   },
   {
     title: tabs[1]?.title || 'Madeira', 
     content: dashboardContent
   }
 ];

 return (
   <div className={`dashboard-indicadores-container ${className || ''}`}>
     <CustomTabs 
       tabs={tabData}
       onTabChange={handleTabChange}
       defaultActiveTab={initialMapType === 'Continental' ? 0 : 1}
       ariaLabel="Selecionar região do dashboard de indicadores"
     />
   </div>
 );
}
